import { Outlet, ScrollRestoration } from 'react-router-dom'
import { Navbar } from './navbar'
import { Footer } from './footer'
import { PageTransition } from '@/shared/motion/page-transition'
import { usePageview } from '@/shared/hooks/use-pageview'
import { AnnouncementBanner } from '@/shared/ui/announcement-banner'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import { useLocaleSync } from '@/shared/i18n/use-locale-sync'
import { PageStatusGate } from '@/features/page-status/page-status-gate'
import { usePageviewTracker } from '@/features/analytics/tracker'
import { CommandPaletteMount } from '@/shared/search/command-palette-mount'
import { AssistantLauncher } from '@/features/assistant/components/assistant-launcher'

const T = defineT({
  skipToMain: { ar: 'تخطّ إلى المحتوى', en: 'Skip to main content' },
})

/**
 * Public-facing layout. Used by every route except admin and auth.
 * Locks the page chrome (navbar + footer) so individual pages stay focused.
 */
export function PublicLayout() {
  const { t } = useTranslation(T)
  usePageview()
  usePageviewTracker()
  // Read `?lang=ar|en` and switch the persisted locale to match.
  // Drives the hreflang alternates emitted by `<Seo>` (Audit Issue 3).
  useLocaleSync()
  return (
    <div className="relative min-h-dvh">
      {/* Skip-to-main: invisible until focused (a11y) */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-[60] focus:inline-flex focus:h-10 focus:items-center focus:gap-2 focus:rounded-full focus:bg-primary focus:px-4 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-lg"
      >
        {t('skipToMain')}
      </a>
      <AnnouncementBanner />
      {/* Decorative background — kept here so it's behind ALL public pages */}
      <div
        className="pointer-events-none fixed inset-0 -z-10 bg-mesh-gradient opacity-60"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 -z-10 grid-bg opacity-30"
        aria-hidden
      />

      <Navbar />
      <main id="main" className="pt-20">
        <PageStatusGate>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </PageStatusGate>
      </main>
      <Footer />
      <ScrollRestoration />
      <CommandPaletteMount />
      {/* Rides every public page; hides itself on /assistant. */}
      <AssistantLauncher />
    </div>
  )
}
